import {useMemo, useRef, useState} from 'react';

import {PreSaveImmediatelyMiddleware} from 'procedures/ProcedurePageContext';
import removeNulls from 'util/removeNulls';

type Middleware = Record<string, {methodCid: string; fn: PreSaveImmediatelyMiddleware} | null>;

/**
 * Downstream components can register middleware, which is executed during `saveImmediately`.
 * This hook manages the list of middleware functions, but does not execute them.
 *
 * Note that while the internals of the hook are agnostic, it is only needed for legacy procedures.
 */
export default function useLegacyPreSaveImmediatelyMiddleware(): {
  addLegacyMiddleware: (methodId: string, fn: PreSaveImmediatelyMiddleware) => string;
  removeLegacyMiddleware: (id: string) => void;
  legacyMiddleware: Array<{methodCid: string; fn: PreSaveImmediatelyMiddleware}>;
} {
  const [middleware, setMiddleware] = useState<Middleware>({});
  const nextId = useRef(1);

  const addLegacyMiddleware = (methodCid: string, fn: PreSaveImmediatelyMiddleware): string => {
    const id = `mw${nextId.current++}`;
    setMiddleware((m) => ({...m, [id]: {fn, methodCid}}));
    return id;
  };

  const removeLegacyMiddleware = (id: string): void => {
    setMiddleware((m) => ({...m, [id]: null}));
  };

  const middlewareWithoutNulls = useMemo(() => removeNulls(Object.values(middleware)), [middleware]);

  return {
    addLegacyMiddleware,
    removeLegacyMiddleware,
    legacyMiddleware: middlewareWithoutNulls,
  };
}
